import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, Users, Star, MessageSquareText, Video, Megaphone, Box, Heart } from "lucide-react";
import { fetchExperimentStats } from "@/lib/metabaseStats";
import { Skeleton } from "@/components/ui/skeleton";
import MetricCard from "@/components/MetricCard";
import { useNavigate } from "react-router-dom";

interface ExperimentStatsCardsProps {
  experimentId: string;
}

const iconFor = (name: string) => {
  const key = name.toLowerCase();
  if (key.includes("experiment")) return <Activity className="h-4 w-4" />;
  if (key.includes("participant")) return <Users className="h-4 w-4" />;
  if (key === "total product logs" || key.includes("product log")) return <Box className="h-4 w-4" />;
  if (key.includes("review")) return <MessageSquareText className="h-4 w-4" />;
  if (key.includes("rating")) return <Star className="h-4 w-4" />;
  if (key.includes("video")) return <Video className="h-4 w-4" />;
  if (key.includes("social")) return <Megaphone className="h-4 w-4" />;
  if (key.includes("recommend")) return <Heart className="h-4 w-4" />;
  return <Activity className="h-4 w-4" />;
};

const ExperimentStatsCards: React.FC<ExperimentStatsCardsProps> = ({ experimentId }) => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["experiment-stats", experimentId],
    queryFn: async () => fetchExperimentStats([experimentId]),
    enabled: !!experimentId,
  });

  // Check if a metric should be clickable and navigate to tastermonials
  const isClickableMetric = (metricName: string) => {
    const clickableMetrics = [
      "social media posts",
      "video submissions",
      "would recommend product",
      "average rating",
      "reviews collected"
    ];
    return clickableMetrics.some(metric =>
      metricName.toLowerCase().includes(metric.toLowerCase())
    );
  };

  const handleMetricClick = (metricName: string) => {
    if (isClickableMetric(metricName)) {
      navigate("/dashboard/tastermonials");
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, idx) => (
          <Skeleton key={idx} className="h-32" />
        ))}
      </div>
    );
  }

  if (error || !data) return null;

  const entries = Object.entries(data);

  if (entries.length === 0) return null;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {entries.map(([name, data]) => (
        <MetricCard
          key={name}
          title={name}
          value={data.value}
          icon={iconFor(name)}
          description={data.description}
          clickable={isClickableMetric(name)}
          onClick={() => handleMetricClick(name)}
        />
      ))}
    </div>
  );
};

export default ExperimentStatsCards;
